import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const BASE = process.env.PUBLIC_URL || '';
const APP_STORE_URL = 'https://apps.apple.com/app/id362278797';
const APP_STORE_BADGE = `${BASE}/Download_on_the_App_Store_Badge_US.svg`;

const SCREENSHOTS = [
  { src: `${BASE}/chainlinkpro/0x0ss.png`, alt: 'Chain Link Pro gameplay screenshot 1' },
  { src: `${BASE}/chainlinkpro/0x1ss.png`, alt: 'Chain Link Pro gameplay screenshot 2' },
  { src: `${BASE}/chainlinkpro/0x2ss.png`, alt: 'Chain Link Pro gameplay screenshot 3' },
  { src: `${BASE}/chainlinkpro/0x4ss.png`, alt: 'Chain Link Pro gameplay screenshot 4' },
];

function HeroCarousel({ images }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(i => (i + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="hero-carousel">
      <div className="hero-carousel__frame">
        {images.map((img, i) => (
          <img
            key={i}
            src={img.src}
            alt={img.alt}
            className={`hero-carousel__img${i === current ? ' hero-carousel__img--active' : ''}`}
          />
        ))}
      </div>
      <div className="hero-carousel__dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`hero-carousel__dot${i === current ? ' hero-carousel__dot--active' : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`View screenshot ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const GAME_MODES = [
  {
    icon: '⚡',
    title: 'Hyper Chain',
    desc: '60-second speed challenge. Link as many matching shapes as you can before time runs out.',
  },
  {
    icon: '⏱️',
    title: 'Timed Mode',
    desc: 'Race against the clock: earn more time with every chain you complete.',
  },
  {
    icon: '🕹️',
    title: 'Arcade Mode',
    desc: 'No time limits. Chain shapes at your own pace and aim for the highest score.',
  },
  {
    icon: '🔲',
    title: 'Static Mode',
    desc: 'Shapes sit on a grid. You have three lives; think carefully before each chain.',
  },
  {
    icon: '🔗',
    title: 'Chain Pattern',
    desc: 'Match a displayed sequence of shapes in the correct order to rack up bonus points.',
  },
  {
    icon: '🌀',
    title: 'Chain Entity',
    desc: 'A growing shape mechanic that adds a twist to every chain you form.',
  },
];

const FEATURES = [
  {
    icon: '✨',
    title: '17 Power-Ups',
    desc: 'Miniature chains, multi-colored variants, metamorphosis, invincibility, and more. Unlock them all through the quest system.',
  },
  {
    icon: '🎯',
    title: 'Quest System',
    desc: 'Complete quests to progressively unlock new power-ups and keep the game feeling fresh.',
  },
  {
    icon: '📈',
    title: 'Score Multipliers',
    desc: 'Earn Speedster bonuses for fast chaining and Repeater rewards for consistency.',
  },
  {
    icon: '🏆',
    title: 'Game Center',
    desc: 'Compete on leaderboards and unlock achievements. See how you rank against players worldwide.',
  },
  {
    icon: '🎵',
    title: '3D Audio & Custom Music',
    desc: 'Immersive 3D sound with headphones, plus the ability to play your own music library in-game.',
  },
  {
    icon: '⚙️',
    title: 'Three Difficulty Levels',
    desc: 'Easy, medium, and hard modes, plus auto-save and auto-pause so you never lose progress.',
  },
];

function useScrollAnimations() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = document.querySelectorAll('.animate-on-scroll');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function ChainLinkPro() {
  useScrollAnimations();

  return (
    <div className="detail-page">
      {/* ── Hero ── */}
      <section className="detail-hero detail-hero--clp">
        <div className="hero__orb hero__orb--blue" style={{ opacity: 0.12 }} />
        <div className="hero__orb hero__orb--purple" style={{ opacity: 0.1 }} />

        <div className="detail-hero__inner">
          <div className="detail-hero__grid">
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'rgba(250,204,21,0.15)',
                  border: '1px solid rgba(250,204,21,0.4)',
                  borderRadius: '999px',
                  padding: '0.25rem 0.875rem',
                  marginBottom: '1rem',
                  color: '#fde68a',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                }}
              >
                ★ Featured by Apple: Hot New Games &amp; What's Hot
              </div>

              <h1 className="detail-hero__title">Chain Link Pro</h1>
              <p className="detail-hero__desc">
                The original award-winning chain puzzle game featured by Apple. Link
                matching colored shapes by dragging across the screen toward a center
                vortex. With 6 game modes, 17 power-ups, and Game Center support, the
                fun never stops.
              </p>

              <div className="detail-hero__download-btns">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={APP_STORE_BADGE} alt="Download on the App Store" className="btn-download-badge" />
                </a>
              </div>

              <div className="detail-hero__stats">
                <div className="detail-stat">
                  <div className="detail-stat__icon">⬇️</div>
                  <div>
                    <div className="detail-stat__value">400,000+</div>
                    <div className="detail-stat__label">Downloads</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">🎮</div>
                  <div>
                    <div className="detail-stat__value">6</div>
                    <div className="detail-stat__label">Game Modes</div>
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat__icon">✨</div>
                  <div>
                    <div className="detail-stat__value">17</div>
                    <div className="detail-stat__label">Power-Ups</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="detail-hero__visual">
              <HeroCarousel images={SCREENSHOTS} />
              <div className="detail-hero__glow detail-hero__glow--clp" />
            </div>
          </div>
        </div>

        <div className="hero__scroll-indicator">
          <div className="hero__scroll-dot" />
        </div>
      </section>

      {/* ── About the Game ── */}
      <section
        style={{
          padding: '4rem 1.5rem',
          background: '#f8fafc',
          textAlign: 'center',
        }}
      >
        <div
          className="animate-on-scroll"
          style={{ maxWidth: 720, margin: '0 auto' }}
        >
          <h2 className="section-title" style={{ marginBottom: '1.25rem' }}>
            The Game That Made{' '}
            <span className="gradient-text">Apple Take Notice</span>
          </h2>
          <p
            style={{
              fontSize: '1.0625rem',
              color: '#64748b',
              lineHeight: 1.7,
              marginBottom: '1rem',
            }}
          >
            PocketGamer said: <em>"When something original turns up in the match-three
            puzzler genre, it makes us sit up and take notice."</em> Chain Link Pro
            achieved top 10 status in the App Store, featured by Apple across multiple
            editorial placements.
          </p>
          <p
            style={{
              fontSize: '1.0625rem',
              color: '#64748b',
              lineHeight: 1.7,
            }}
          >
            Players link matching colored shapes by dragging across the screen toward a
            center vortex. Chain speed, combo multipliers, and 17 unique power-ups make
            every session different. Available for iPhone, iPad, and Apple Vision Pro.
          </p>
        </div>
      </section>

      {/* ── Game Modes ── */}
      <section className="detail-features">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            6 Game <span className="gradient-text">Modes</span>
          </h2>
          <p className="section-subtitle">
            Every mode offers a completely different challenge; find your favourite.
          </p>
        </div>

        <div className="features-grid">
          {GAME_MODES.map((m, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--clp">
                {m.icon}
              </div>
              <h3 className="feature-card__title">{m.title}</h3>
              <p className="feature-card__desc">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="detail-features" style={{ background: '#f8fafc' }}>
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            Packed with <span className="gradient-text">Features</span>
          </h2>
          <p className="section-subtitle">
            Everything you'd want in a premium puzzle game, and then some.
          </p>
        </div>

        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className={`feature-card animate-on-scroll stagger-${(i % 3) + 1}`}
            >
              <div className="feature-card__icon feature-card__icon--clp">
                {f.icon}
              </div>
              <h3 className="feature-card__title">{f.title}</h3>
              <p className="feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="benefits-section">
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            Why Play <span className="gradient-text">Chain Link Pro</span>
          </h2>
        </div>

        <div className="benefits-grid">
          <div className="benefit-card benefit-card--blue animate-on-scroll stagger-1">
            <div className="benefit-card__icon benefit-card__icon--blue">🧠</div>
            <h3 className="benefit-card__title">Genuinely Original</h3>
            <p className="benefit-card__desc">
              A fresh take on the puzzle genre that Apple editors felt was worth
              featuring multiple times. Not another clone: something new.
            </p>
          </div>
          <div className="benefit-card benefit-card--purple animate-on-scroll stagger-2">
            <div className="benefit-card__icon benefit-card__icon--purple">🔁</div>
            <h3 className="benefit-card__title">Infinitely Replayable</h3>
            <p className="benefit-card__desc">
              Six modes, three difficulty levels, 17 power-ups, and Game Center
              leaderboards keep every session fresh and competitive.
            </p>
          </div>
          <div className="benefit-card benefit-card--amber animate-on-scroll stagger-3">
            <div className="benefit-card__icon benefit-card__icon--amber">🎮</div>
            <h3 className="benefit-card__title">Play Anywhere</h3>
            <p className="benefit-card__desc">
              Auto-save and auto-pause mean you can pick up right where you left off:
              commute, waiting room, or sofa.
            </p>
          </div>
        </div>
      </section>

      {/* ── In-App Purchases ── */}
      <section style={{ padding: '4rem 1.5rem', background: '#f8fafc' }}>
        <div className="section-header animate-on-scroll">
          <h2 className="section-title">
            In-App <span className="gradient-text">Purchases</span>
          </h2>
          <p className="section-subtitle">
            The base game is $0.99. Optional add-ons expand your experience.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            maxWidth: 900,
            margin: '0 auto',
          }}
        >
          {[
            { name: 'Unlock Chain Entity', price: '$0.99' },
            { name: 'Unlock Chain Pattern', price: '$0.99' },
            { name: '5 Extra Lives', price: '$0.99' },
            { name: 'Unlock All Power-Ups', price: '$0.99' },
          ].map((iap) => (
            <div
              key={iap.name}
              className="animate-on-scroll"
              style={{
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 12,
                padding: '1.25rem 1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9375rem' }}>
                {iap.name}
              </span>
              <span
                style={{
                  fontWeight: 700,
                  color: '#2563eb',
                  fontSize: '1rem',
                  whiteSpace: 'nowrap',
                  marginLeft: '1rem',
                }}
              >
                {iap.price}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="detail-cta detail-cta--clp">
        <div className="detail-cta__orb" />
        <div className="detail-cta__inner">
          <h2 className="detail-cta__title">Ready to Start Chaining?</h2>
          <p className="detail-cta__subtitle">
            Join 400,000+ players who discovered why Apple featured Chain Link Pro.
            Only $0.99 on the App Store.
          </p>
          <div className="detail-cta__btns">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={APP_STORE_BADGE} alt="Download on the App Store" className="btn-download-badge" />
            </a>
            <Link to="/" className="btn-download btn-download--outline">
              ← Explore Other Apps
            </Link>
          </div>
          <div className="app-page-footer">
            <span>© {new Date().getFullYear()} Software Beyond Limits. All rights reserved.</span>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ChainLinkPro;
